import React, {useState} from "react";
import {useNavigate} from "react-router-dom";
import {Box, IconButton, Typography, useTheme} from "@material-ui/core";
import {grey} from "@material-ui/core/colors";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import ReplyIcon from "@material-ui/icons/Reply";
import DoneAllIcon from "@material-ui/icons/DoneAll";
import ContractorChip from "view/components/DataDisplay/ContractorChip";
import FlexBox from "view/components/Layout/FlexBox";
import clsx from "clsx";
import {MessageFile} from "ts/api/chat";
import TruncatedText from "view/components/TruncatedText";
import Avatar from "../../../Avatar";
import LoadedFile from "../LoadedFile";
import {useStyles} from "./style";

type BubbleProps = {
  showId: string;
  standId: string;
  exhibitorId?: string;
  exhibitorPairId?: string;
  id: string;
  index: number;
  isSender: boolean;
  isDeleted?: boolean;
  isEdited?: boolean;
  replayId?: string;
  replayText?: string;
  text: string;
  fullName?: string;
  contractorCompanyName?: string;
  subName?: string;
  time?: string;
  files?: MessageFile[];
  read: boolean;
  userRole?: string;
  currentRole?: string;
  img?: string;
  onEdit: (text: string, id: string) => void;
  onDelete: (
    id: string,
    type: string,
    fileId?: string,
    fileName?: string
  ) => void;
  onReplay: (text: string, id: string) => void;
  goToReplayRef: (id: string) => void;
};

const Bubble = ({
  showId,
  standId,
  exhibitorId,
  exhibitorPairId,
  id,
  index,
  isSender,
  isDeleted,
  isEdited,
  replayId,
  replayText,
  text,
  fullName,
  contractorCompanyName,
  img,
  subName,
  time,
  files,
  read,
  userRole,
  onEdit,
  onDelete,
  onReplay,
  goToReplayRef,
  currentRole,
}: BubbleProps) => {
  const history = useNavigate();
  const [isHovered, setIsHovered] = useState(false);
  const classes = useStyles();
  const theme = useTheme();
  const deletedText = "This message was deleted";
  const goToExhibitor = () => {
    if (userRole === "organiser" && !isSender && exhibitorPairId) {
      const path = `/shows/${showId}/exhibitor-pair/${exhibitorPairId}`;

      history(path);
    }
  };

  const textWithLinks = (text: string) => {
    const urlRegex = /(\bhttps?:\/\/[-A-Z0-9+&@#/%=~_|!:,.;]*[-A-Z0-9+&@#/%=~_|])/gi;
    const parts = text.split(urlRegex);

    return (
      <div style={{display: "inline-block"}}>
        {parts.map((part, index) =>
          urlRegex.test(part) ? (
            <a
              key={index}
              href={part}
              target="_blank"
              rel="noopener noreferrer"
            >
              {part}
            </a>
          ) : (
            part
          )
        )}
      </div>
    );
  };

  const noData = !text && (!files || (files && files.length === 0));
  return (
    <Box
      className={`${classes.bubbleContainer} ${
        isSender ? classes.bubbleContainerRight : classes.bubbleContainerLeft
      }`}
    >
      <Box
        className={isSender ? classes.nameRight : classes.nameLeft}
        padding="24px"
      >
        <Avatar img={img || ""} name={fullName as string} />
        <Box margin="0 16px">
          <FlexBox gap={1} style={{justifyContent: isSender ? "end" : "start"}}>
            <Typography
              onClick={goToExhibitor}
              className={
                userRole === "organiser" && !isSender
                  ? classes.nameLink
                  : classes.name
              }
            >
              {fullName}
            </Typography>
            {currentRole === "organiser" && contractorCompanyName && (
              <ContractorChip companyName={contractorCompanyName} />
            )}
          </FlexBox>
          <TruncatedText
            text={subName || ""}
            maxLength={50}
            className={classes.subName}
          />
        </Box>
      </Box>
      {!!files?.length &&
        files.map((file) => (
          <Box
            key={file?.id}
            className={isSender ? classes.fileRight : classes.fileLeft}
          >
            <LoadedFile
              file={file}
              isSender={isSender}
              messageId={id}
              onDelete={(messageId, type, fileId, fileName) =>
                onDelete(messageId, type, fileId, fileName)
              }
            />
          </Box>
        ))}
      <Box
        className={`${classes.bubbleMessageContainer} ${
          isSender ? classes.bubbleContainerRight : classes.bubbleContainerLeft
        }`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {isSender && isHovered && !isDeleted && !noData && (
          <Box className={classes.actionButtons}>
            <IconButton size="small" onClick={() => onDelete(id, "message")}>
              <DeleteIcon htmlColor={theme.palette.contrastIcon.main} />
            </IconButton>
            <IconButton size="small" onClick={() => onEdit(text, id)}>
              <EditIcon htmlColor={theme.palette.contrastIcon.main} />
            </IconButton>
            <IconButton size="small" onClick={() => onReplay(text, id)}>
              <ReplyIcon htmlColor={theme.palette.contrastIcon.main} />
            </IconButton>
          </Box>
        )}
        {(text || isDeleted || noData) && (
          <Box
            className={`${classes.bubble} ${
              isSender ? classes.bubbleBlue : classes.bubbleWhite
            }`}
          >
            {replayId && replayText && !isDeleted && (
              <Box
                onClick={() => goToReplayRef(replayId)}
                className={classes.replayContainer}
              >
                <span
                  style={{
                    height: "18px",
                    width: "2px",
                    backgroundColor: theme.palette.primary.main,
                    position: "absolute",
                    left: "10px",
                  }}
                />
                <Typography noWrap className={classes.replayText}>
                  {replayText}
                </Typography>
              </Box>
            )}
            <Box className={clsx({[classes.deletedText]: isDeleted || noData})}>
              {!isDeleted && textWithLinks(text || "")}
              {(isDeleted || noData) && deletedText}{" "}
              {isEdited && !isDeleted && (
                <Box
                  display="inline-block"
                  className={clsx({[classes.deletedText]: isEdited})}
                >
                  (edited)
                </Box>
              )}
            </Box>

            <Box
              className={clsx(isSender ? classes.timeRight : classes.timeLeft)}
            >
              <span className={classes.subName}>{time} </span>
              {!isDeleted && !noData && isSender && (
                <DoneAllIcon
                  style={{
                    color: read ? theme.palette.primary.main : grey[400],
                    fontSize: "18px",
                  }}
                />
              )}
            </Box>
          </Box>
        )}
        {!isSender && isHovered && !isDeleted && (
          <Box className={classes.actionButtons} marginLeft="8px">
            <IconButton size="small" onClick={() => onReplay(text, id)}>
              <ReplyIcon htmlColor={theme.palette.contrastIcon.main} />
            </IconButton>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Bubble;
